import type { CompanyLpnSettingExtension as CompanyLpnSetting } from 'tio-data/data/resources/company-lpn-setting.ext';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import { companyLpnSettingFor } from 'tio-data/data/resources/lpn-page.ext';
import { buildLpnCardView } from './card-view.ts';

/** How one catalog page appears to one company. */
export interface LpnListing {
  page: LpnPage;
  /** The company's override, when it has one. */
  setting: CompanyLpnSetting | undefined;
  /** False when the company disabled the page, or another company created it. */
  isVisible: boolean;
  /** Recommended by the company's setting; a company's own pages always count. */
  isEmployerRecommended: boolean;
}

/**
 * Resolves a catalog page against the viewer's company. LPN is opt-out: a page is listed unless the
 * company disabled it, and a company-created page is listed only for the company that owns it.
 *
 * @param companySettingId the viewer's company setting
 */
export function lpnListingFor(page: LpnPage, companySettingId: string): LpnListing {
  const setting = companyLpnSettingFor(page, companySettingId);
  const isCompanyCreated = page.isCompanyCreated === true;
  const createdByOtherCompany = isCompanyCreated && !setting;
  return {
    page,
    setting,
    isVisible: !createdByOtherCompany && (setting?.isEnabled ?? true),
    isEmployerRecommended: setting?.employerRecommended === true || isCompanyCreated,
  };
}

/**
 * Whether a school shows every selected savings badge. It reads the same merged view the card
 * displays, so a filtered school always shows the badge it was filtered for. The API's savings
 * filter reads only the global badges, so this runs after it.
 *
 * @param savings selected keys from SAVINGS_OPTIONS; empty passes everything
 */
export function passesLpnSavings(
  page: LpnPage,
  setting: CompanyLpnSetting | undefined,
  savings: readonly string[]
): boolean {
  if (!savings.length) return true;
  const view = buildLpnCardView(page, setting);
  return savings.every((tag) => {
    switch (tag) {
      case 'capped_tuition':
        return Boolean(view.badgeCappedTuition);
      case 'family_eligible':
        return Boolean(view.badgeFamilySavings);
      case 'no_fees':
        return Boolean(view.badgeFeesWaived);
      default:
        return true;
    }
  });
}
